import { faBriefcase, faWarning } from "@fortawesome/pro-regular-svg-icons";

import { canRetryPersonSearch, EXCLUSION_REASON_PARAMS } from "@signalist/shared-types";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { TableColumn } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { SortParams } from "@/types/types";

import { ListPerson } from "../../schema/list";
import {
	getReasonLabel,
	isRecoverableReason,
	visibleExclusionReasons,
} from "../exclusionReasonMeta";
import { getOrderStatus } from "../column-utils";
import { RowRetryButton } from "./RowRetryButton";

/** Per-row actions for the Reason column (wired by the list table; optional elsewhere). */
export interface ExclusionReasonColumnActions {
	/** Recover a title-excluded lead (0.9-credit re-enrich, PREC-3536 Phase 5). */
	onEnrichAnyway?: (row: ListPerson) => void;
	/**
	 * Search a lead again: a Not found one (PREC-4520), or an Unsure one whose employer could not be
	 * verified (PREC-4522); charged only when it finds someone.
	 */
	onRetry?: (row: ListPerson) => void | Promise<void>;
}

const findTitleExcludedReason = (row: ListPerson) =>
	visibleExclusionReasons(row).find((reason) => isRecoverableReason(reason.filterParam));

/**
 * A lead a user took off the blacklist carries who did it (PREC-2548). That explains why it is in
 * Found; it is not a filter it failed, so it is shown as the sentence it is.
 */
const findIncludedByUserReason = (row: ListPerson) =>
	visibleExclusionReasons(row).find(
		(reason) => reason.filterParam === EXCLUSION_REASON_PARAMS.includedByUser
	);

/**
 * Retry is offered where the API will search the lead again (PREC-4520, PREC-4524): the one rule
 * both apps read (`canRetryPersonSearch`, @signalist/shared-types). It used to be offered on every
 * "person" reason, and then on company-list leads the API had no CSV person row for.
 */
const hasRetryableReason = (row: ListPerson & { csvEnrichmentPersonId?: number | null }) =>
	canRetryPersonSearch({
		inclusionStatus: row.inclusionStatus,
		// Sent with every row of list/unassigned (the lead's own column), not in ListPersonSchema.
		csvEnrichmentPersonId: row.csvEnrichmentPersonId,
		listSourceType: row.list?.sourceType,
		reasonParams: visibleExclusionReasons(row).map((reason) => reason.filterParam),
	});

/** The amber "Title excluded" tag (briefcase icon + tooltip) of the recovery surface. */
const renderTitleExcludedTag = (
	ts: (key: string) => string,
	row: ListPerson,
	actions?: ExclusionReasonColumnActions
) => {
	// PREC-3536 Fix B: show the AI reasoning behind the exclusion when present (the pipeline stores
	// it as the reason text); fall back to the generic tooltip for manual/unscreened titles.
	const reasoning = findTitleExcludedReason(row)?.reason?.trim();

	return (
		<div className="flex items-center gap-2" data-test="title-excluded-cell">
			<Tooltip content={reasoning || ts("reasonTag.titleExcludedTooltip")} panel={!!reasoning}>
				<span
					className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-status-warning-light px-2 py-0.5 text-xs font-semibold text-status-warning cursor-pointer"
					data-test="title-excluded-tag"
				>
					<Icon className="h-3 w-3" icon={faBriefcase} />
					{ts("reasonTag.titleExcluded")}
				</span>
			</Tooltip>
			{actions?.onEnrichAnyway && (
				<Button
					className="!h-[24px] !w-fit whitespace-nowrap !px-2 !text-xs"
					size={ButtonSize.XS}
					testId="row-enrich-anyway"
					title={ts("reasonTag.enrichAnyway")}
					onClick={(event) => {
						event.stopPropagation();
						actions.onEnrichAnyway?.(row);
					}}
				/>
			)}
		</div>
	);
};

/** Reasons whose text names the user who decided (PREC-4172, PREC-2548): shown as the sentence. */
const SENTENCE_REASON_PARAMS: ReadonlySet<string> = new Set([
	EXCLUSION_REASON_PARAMS.manuallyExcluded,
	EXCLUSION_REASON_PARAMS.includedByUser,
]);

/**
 * A reason's name in the cell: the label the Show dropdown names it by. `ts(filterParam)` printed
 * the raw key for every param without a top-level translation ("inbox.wrongCompany",
 * "inbox.processingError"), and `company` printed its stored text where the Show filter says
 * "Doesn't work there anymore" (PREC-4519; PREC-4083 did the same for the blacklist verdicts).
 */
const legacyReasonLabel = (
	ts: (key: string) => string,
	row: ListPerson,
	reason: { filterParam?: string | null; reason: string }
): string =>
	reason.filterParam && !SENTENCE_REASON_PARAMS.has(reason.filterParam)
		? getReasonLabel(ts, row.inclusionStatus ?? "", reason.filterParam)
		: reason.reason;

/** The pre-existing "N Filters not passed (…)" rendering. */
const renderLegacyReasons = (ts: (key: string) => string, row: ListPerson) => (
	<Tooltip
		content={
			<div>
				{visibleExclusionReasons(row).map((reason) => {
					const params = reason.reason.split(":");
					const paramter = params[0];
					const value = params[1];

					return (
						<div key={reason.reason} className="mb-1">
							{paramter && (
								<span className="font-bold">
									{params.length > 1 ? <>{ts(paramter)}:</> : <>{paramter}</>}
								</span>
							)}{" "}
							{value && (
								<span
									dangerouslySetInnerHTML={{
										__html: value
											?.replaceAll('["', "")
											?.replaceAll('"]', "")
											?.replaceAll('","', ", ")
											?.replace(
												reason?.filterValue ?? "",
												reason?.filterValue
													? `<span class="text-text-meta italic font-bold">${reason?.filterValue}</span>`
													: ""
											),
									}}
									className="text-text-meta"
								></span>
							)}
						</div>
					);
				})}
			</div>
		}
	>
		<div className="text-ellipsis overflow-hidden max-w-[450px] cursor-pointer">
			{row.exclusionReasons ? (
				<>
					{visibleExclusionReasons(row).length || 0} Filter
					{visibleExclusionReasons(row).length > 1 ? "s" : ""} not passed (
					{visibleExclusionReasons(row)
						.map((reason) => legacyReasonLabel(ts, row, reason))
						.join(", ")}
					)
				</>
			) : (
				""
			)}
			{row.exclusionReason === "INVALID_RESULT"
				? "Person/company doesn't match the provided company URL."
				: row.exclusionReason}
		</div>
	</Tooltip>
);

const renderLegacySingleReason = (ts: (key: string) => string, row: ListPerson) => (
	<Tooltip
		content={
			<div>
				{row.exclusionReason && (
					<div key={row.exclusionReason} className="mb-1">
						<span className="font-bold">{ts("exclusionReason")}:</span> {}
						<span
							dangerouslySetInnerHTML={{
								__html: row.exclusionReason,
							}}
							className="text-text-meta"
						></span>
					</div>
				)}
			</div>
		}
	>
		<div className="text-ellipsis overflow-hidden max-w-[450px] cursor-pointer">
			{row.exclusionReason}
		</div>
	</Tooltip>
);

/**
 * The Reason column (moved out of `allColumns.tsx`, PREC-3536 Phase 5): a title-excluded
 * lead renders the recoverable amber tag (+ "Enrich anyway" when wired); every other reason
 * keeps the historical rendering, with a plain Retry for retryable person-not-found rows.
 */
export const renderExclusionReasonColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams,
	actions?: ExclusionReasonColumnActions
): TableColumn => {
	const orderTitle = "exclusion_reason.reason";

	return {
		title: ts("exclusionReason"),
		icon: faWarning,
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "exclusionReason",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			if (findTitleExcludedReason(row)) {
				return renderTitleExcludedTag(ts, row, actions);
			}

			const includedByUser = findIncludedByUserReason(row);

			if (includedByUser && visibleExclusionReasons(row).length === 1) {
				return (
					<span className="text-text-meta" data-test="included-by-user">
						{includedByUser.reason}
					</span>
				);
			}

			return (
				<div className="flex items-center gap-2">
					{visibleExclusionReasons(row).length > 0 && renderLegacyReasons(ts, row)}
					{row.exclusionReason &&
						row.exclusionReasons &&
						visibleExclusionReasons(row).length === 0 &&
						renderLegacySingleReason(ts, row)}
					{actions?.onRetry && hasRetryableReason(row) && (
						<RowRetryButton title={ts("reasonTag.retry")} onRetry={() => actions.onRetry?.(row)} />
					)}
				</div>
			);
		},
	};
};
